import { useState } from "react";
import { toast } from "sonner";
import { submitEnquiry, apiErr } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../ui/dialog";

const DECK_URL = "/partecon-pitch-deck.pdf";

export const InvestorDialog = ({ children, title = "Request Investor Access", description, submitLabel = "Submit Request", deckOnSuccess = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  const payload = () => ({
    name: form.name,
    email: form.email,
    school: form.company,
    role: "Investor",
    interest: title,
    message: form.message,
    source: deckOnSuccess ? "investor-deck" : "investor-access",
  });

  const reset = () => setForm({ name: "", email: "", company: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    if (deckOnSuccess) {
      // Open in same user gesture to avoid popup blocking; save lead in background.
      window.open(DECK_URL, "_blank", "noopener,noreferrer");
      trackEvent("pitch_deck_access", { source: "investor-deck", interest: title });
      submitEnquiry(payload()).catch(() => {});
      setOpen(false);
      reset();
      toast.success("Opening the pitch deck — thank you.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry(payload());
      trackEvent("investor_access_request", { source: "investor-access", interest: title });
      setOpen(false);
      reset();
      toast.success("Thank you — our team will be in touch regarding investor access.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-[#9DA9BA]/60 focus:border-[#6366F1]/50 focus:outline-none transition-colors";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-[#0E1117] border border-white/10 text-white sm:max-w-lg" data-testid="investor-dialog">
        <DialogHeader>
          <span className="overline">PARTECON · Investors</span>
          <DialogTitle className="font-heading text-2xl font-medium tracking-tight mt-2">{title}</DialogTitle>
          {description && <DialogDescription className="text-[#9DA9BA]">{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="investor-form">
          <input data-testid="investor-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="investor-email" type="email" className={field} placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="investor-company" className={field} placeholder="Company / fund name (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <textarea data-testid="investor-message" className={field} rows={3} placeholder="Short message (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button type="submit" disabled={loading} data-testid="investor-submit" className="btn-primary w-full py-3 text-sm disabled:opacity-60">{loading ? "Submitting…" : submitLabel}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
